import {
  invalidateManualPaymentSlipsQueries,
  useRegisterManualPaymentSlipDownload,
  useUpdateManualPaymentSlipPaid,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { capitalize, formatCurrency, formatDate, toast } from '@/helpers'
import {
  ManualPaymentSlip,
  ManualPaymentSlipTypeLabel,
  MonthsEnum,
} from '@/types'
import { Box, Card, FormControlLabel, Switch, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { GuideActionButton } from './guide-action-button'

type ManualPaymentSlipCardProps = {
  slip: ManualPaymentSlip
}

function manualSlipLabel(slip: ManualPaymentSlip) {
  const description = slip.other_description?.trim()
  const typeLabel =
    slip.type === 'other' && description
      ? description
      : ManualPaymentSlipTypeLabel[slip.type]

  if (slip.month == null || slip.year == null) return typeLabel

  return `${typeLabel} - ${MonthsEnum[slip.month]}/${slip.year}`
}

export function ManualPaymentSlipCard({ slip }: ManualPaymentSlipCardProps) {
  const { company } = useCompany()
  const queryClient = useQueryClient()

  const { mutate, isPending, variables } = useUpdateManualPaymentSlipPaid({
    onSuccess: () => {
      invalidateManualPaymentSlipsQueries(queryClient)
    },
    onError: () => {
      toast.error('Não foi possível atualizar o status de pagamento.')
    },
  })

  const { mutate: registerDownload } = useRegisterManualPaymentSlipDownload({
    onError: () => {
      toast.error('Não foi possível registrar o download da guia.')
    },
  })

  const isUpdating = isPending && variables?.slip_key === slip.key

  return (
    <Card sx={{ px: 2, py: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography>{manualSlipLabel(slip)}</Typography>
        <Typography color={slip.paid ? 'success.main' : 'warning.main'}>
          {slip.paid ? 'Pago' : 'Pagamento pendente'}
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={0.5}
      >
        <Typography variant="body2">
          Vencimento: {capitalize(formatDate(slip.due_date))}
        </Typography>
        <Typography variant="h6">
          {formatCurrency(Number(slip.amount))}
        </Typography>
      </Box>

      {slip.observation ? (
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {slip.observation}
        </Typography>
      ) : null}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        gap={1}
      >
        <FormControlLabel
          control={
            <Switch
              checked={slip.paid}
              disabled={isUpdating}
              onChange={(_, checked) =>
                mutate({
                  company_key: company.key,
                  paid: checked,
                  slip_key: slip.key,
                })
              }
            />
          }
          label="Você já pagou?"
        />

        <GuideActionButton
          dueDate={slip.due_date}
          paid={slip.paid}
          url={slip.url}
          onDownload={() => {
            if (!slip.url) return

            registerDownload({
              company_key: company.key,
              slip_key: slip.key,
            })
            window.open(slip.url, '_blank', 'noopener,noreferrer')
          }}
        />
      </Box>
    </Card>
  )
}
